import log from '@/lib/logger';
import type { AppDispatch, RootState } from '@/store';
import type { NormalizedTransactionResponse } from '@/types/client';
import type { SampledValue, Transaction } from '@/types/swagger';
import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import { http } from 'config';
import { transactionByTimestampListSchema, transactionListSchema } from 'features/schemas';
import { loadVehicles } from 'features/Vehicles';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import { callNextPage } from 'util/callNextPage';
import { getNewImmutableState } from 'util/getNewImmutableState';

// ----------------------------------- Actions ---------------------------------
// Action Types
const LOAD_TRANSACTIONS = 'transactions/load_transactions';
const LOAD_SAMPLE_VALUES_BY_TRANSACTION_ID = 'transactions/load_sample_values_by_transaction_id';
const LOAD_CHARGE_POINT_TRANSACTIONS = 'transactions/load_charge_point_transactions';
const SELECT_TRANSACTION = 'transactions/select_transaction';
const UNSELECT_TRANSACTIONS = 'transactions/unselect_transactions';
const TRANSACTIONS_FETCH_START = 'connectors/transactions_fetch_start';
const TRANSACTIONS_FETCH_END = 'connectors/transactions_fetch_end';
export const SAMPLE_VALUES_FETCH_START = 'connectors/sample_values_fetch_start';
export const SAMPLE_VALUES_FETCH_END = 'connectors/sample_values_fetch_end';

const SET_SELECTED_TRANSACTION = 'transactions/set_selected_transaction';
const ADD_CHARGE_POINT_TRANSACTIONS = 'transactions/add_charge_point_transactions';

// Action Creators
export const loadTransactions = (transactions: Record<string, Transaction>) => ({
  type: LOAD_TRANSACTIONS,
  payload: transactions,
});
export const loadChargePointTransactions = (
  chargePointId: number,
  transactionsByTimestamp: Record<string, Transaction>
) => ({
  type: LOAD_CHARGE_POINT_TRANSACTIONS,
  meta: { chargePointId },
  payload: transactionsByTimestamp,
});
export const loadSampledValuesByTransactionId = (
  transactionId: number,
  sampledValues: SampledValue[],
  setValues?: boolean
) => ({
  type: LOAD_SAMPLE_VALUES_BY_TRANSACTION_ID,
  meta: { transactionId, setValues },
  payload: sampledValues,
});
// export const selectTransaction = (transactionTimestamp) => ({
//   type: SELECT_TRANSACTION,
//   payload: transactionTimestamp,
// });
export const unselectTransactions = () => ({ type: UNSELECT_TRANSACTIONS });
export const transactionsFetchStart = () => ({ type: TRANSACTIONS_FETCH_START });
export const transactionsFetchEnd = () => ({ type: TRANSACTIONS_FETCH_END });
export const sampledValuesFetchStart = () => ({
  type: SAMPLE_VALUES_FETCH_START,
});
export const sampledValuesFetchEnd = () => ({ type: SAMPLE_VALUES_FETCH_END });

export const setSelectedTransaction = (transaction: Transaction | null) => ({
  type: SET_SELECTED_TRANSACTION,
  payload: transaction,
});
export const addChargePointTransactions = (
  chargePointId: number,
  transactions: Record<string, Transaction>
) => ({
  type: ADD_CHARGE_POINT_TRANSACTIONS,
  meta: { chargePointId },
  payload: transactions,
});

// ----------------------------------- Reducer ---------------------------------
interface TransactionsState {
  byId: Record<number, Transaction>;
  byChargePointId: Record<number, Record<number, Transaction>>;
  selectedTransactionTimestamp: string;
  selectedTransaction: Transaction | null;
  sampledValuesByTransactionId: Record<number, SampledValue[]>;
  allIds: number[];
  fetching: boolean;
  sampledValuesFetching: boolean;
}

// Init State
const INIT_STATE: TransactionsState = {
  byId: {},
  byChargePointId: {},
  selectedTransactionTimestamp: '',
  selectedTransaction: null,
  sampledValuesByTransactionId: {},
  allIds: [],
  fetching: false,
  sampledValuesFetching: false,
};

// Reducer
const transactionReducer = (state = INIT_STATE, action: UnknownAction): TransactionsState => {
  const { type, meta, payload } = action as PayloadAction<
    Transaction | Transaction[] | SampledValue | SampledValue[] | string
  > & {
    meta: { setValues: boolean; chargePointId: number; transactionId: number };
  };
  switch (type) {
    case LOAD_TRANSACTIONS: {
      return getNewImmutableState(state, payload);
    }
    case LOAD_CHARGE_POINT_TRANSACTIONS: {
      return getNewImmutableState(
        state,
        { [meta.chargePointId]: payload },
        'byChargePointId',
        true
      );
    }
    case LOAD_SAMPLE_VALUES_BY_TRANSACTION_ID: {
      if (meta.setValues)
        return {
          ...state,
          sampledValuesByTransactionId: {
            ...state.sampledValuesByTransactionId,
            [meta.transactionId]: payload as SampledValue[],
          },
        };
      return {
        ...state,
        sampledValuesByTransactionId: {
          ...state.sampledValuesByTransactionId,
          [meta.transactionId]: state.sampledValuesByTransactionId[meta.transactionId]
            ? [...state.sampledValuesByTransactionId[meta.transactionId], payload as SampledValue]
            : [payload as SampledValue],
        },
      };
    }
    case SELECT_TRANSACTION: {
      return { ...state, selectedTransactionTimestamp: payload as string };
    }
    case UNSELECT_TRANSACTIONS: {
      return { ...state, selectedTransactionTimestamp: '' };
    }
    case TRANSACTIONS_FETCH_START: {
      return { ...state, fetching: true };
    }
    case TRANSACTIONS_FETCH_END: {
      return { ...state, fetching: false };
    }
    case SAMPLE_VALUES_FETCH_START: {
      return { ...state, sampledValuesFetching: true };
    }
    case SAMPLE_VALUES_FETCH_END: {
      return { ...state, sampledValuesFetching: false };
    }
    case SET_SELECTED_TRANSACTION: {
      return { ...state, selectedTransaction: payload as Transaction };
    }
    case ADD_CHARGE_POINT_TRANSACTIONS: {
      return {
        ...state,
        byChargePointId: {
          ...state.byChargePointId,
          [meta.chargePointId]: {
            ...state.byChargePointId[meta.chargePointId],
            ...(payload as Transaction[]),
          },
        },
      };
    }
    default: {
      return state;
    }
  }
};

export default transactionReducer;

// ----------------------------------- Side Effects ----------------------------
// Thunks
export const fetchTransactions = () => async (dispatch: AppDispatch) => {
  Nprogress.start();
  const apiTransactions = 'chargepoints/transactions/';
  dispatch(transactionsFetchStart());
  const resolve = (data: unknown) => {
    const normalizedData = normalize(data, transactionListSchema);
    const { transactions, vehicles } = normalizedData.entities;
    if (transactions) dispatch(loadTransactions(transactions));
    if (vehicles) dispatch(loadVehicles(vehicles));
    dispatch(transactionsFetchEnd());
    Nprogress.done();
    return data;
  };
  const reject = (error: AxiosError) => {
    log.error(error);
    dispatch(transactionsFetchEnd());
    Nprogress.done();
    return error;
  };
  return callNextPage(apiTransactions, resolve, reject);
};

export const fetchTransactionsByChargePointId =
  (
    chargePointId: number,
    startDateTime: string,
    endDateTime: string,
    pageSize: number,
    page: number
  ) =>
  async (dispatch: AppDispatch) => {
    Nprogress.start();
    const withDateTime =
      startDateTime && endDateTime ? `/between/${startDateTime}/${endDateTime}` : '';
    const apiTransactions = `chargepoints/transactions/by_charge_point/${chargePointId}${withDateTime}/`;
    dispatch(transactionsFetchStart());
    return http
      .get(apiTransactions, {
        params: {
          page: page || 999,
          page_size: pageSize,
        },
      })
      .then((data) => {
        const normalizedData = normalize(data.data.results, transactionByTimestampListSchema);
        const { transactionsByTimestamp } = normalizedData.entities;
        if (transactionsByTimestamp && Object.keys(transactionsByTimestamp).length) {
          const transactionArray = Object.values(transactionsByTimestamp);
          dispatch(setSelectedTransaction(transactionArray[transactionArray.length - 1]));
          dispatch(loadTransactions(transactionsByTimestamp));
          dispatch(loadChargePointTransactions(chargePointId, transactionsByTimestamp));
        }
        dispatch(transactionsFetchEnd());
        Nprogress.done();
        return data;
      })
      .catch((error) => {
        log.error(error);
        dispatch(transactionsFetchEnd());
        Nprogress.done();
        return error;
      });
  };

export const fetchTransactionsByChargePointIdPaginated =
  (chargePointId: number, pageSize: number, page: number) => async (dispatch: AppDispatch) => {
    Nprogress.start();
    const apiTransactions = `chargepoints/transactions/by_charge_point/${chargePointId}/`;
    return http
      .get(apiTransactions, {
        params: {
          page,
          page_size: pageSize,
        },
      })
      .then((data) => {
        const normalizedData: NormalizedTransactionResponse = normalize(
          data.data.results,
          transactionByTimestampListSchema
        );
        const { transactionsByTimestamp } = normalizedData.entities;
        if (transactionsByTimestamp && Object.keys(transactionsByTimestamp).length) {
          dispatch(loadTransactions(transactionsByTimestamp));
          dispatch(addChargePointTransactions(chargePointId, transactionsByTimestamp));
        }
        Nprogress.done();
        return data.data;
      })
      .catch((error) => {
        log.error(error);
        Nprogress.done();
        return error;
      });
  };

export const selectTransaction =
  (transactionId: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const currentTransactions = getState().transactions.byId;
    const selectedTransaction = currentTransactions[transactionId];
    dispatch(setSelectedTransaction(selectedTransaction));
  };

export const unselectTransaction = () => async (dispatch: AppDispatch) => {
  dispatch(setSelectedTransaction(null));
};

// Selectors
export const getTransactionsSelector = (state: RootState) => state.transactions.byId;
export const getTransactionsByChargePointIdSelector = (state: RootState, chargePointId: number) =>
  state.transactions.byChargePointId[chargePointId];
// export const getSelectedTransactionIdSelector = (state) => state.transactions.selectedTransactionId;
export const getSelectedTransactionSelector = (state: RootState) =>
  state.transactions.selectedTransaction;
export const getTransactionIsFetching = (state: RootState) => state.transactions.fetching;
